import React from 'react';
import { useSpc } from '../context/SpcContext';
import { Logo, motion } from './ui';
import { MessageCircle, Calendar, Trophy, Users, Gift, Award, CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { IMG } from '../assets';

const EXPLAINER_POINTS = [
  { icon: Calendar, title: 'Book every session', text: 'Runs, gym slots, socials and events — reserve your spot in seconds, including block-booked class series.' },
  { icon: Trophy, title: 'Race Hub & Challenges', text: 'Log race times, chase seasonal challenges, and climb the club leaderboards with the crew.' },
  { icon: Users, title: 'A community that shows up', text: 'Group chat, and a clubhouse full of members who train, race and celebrate together.' },
  { icon: Gift, title: 'SPC Points & rewards', text: "Earn points for showing up and referring friends, then spend them on real discounts and perks." },
  { icon: Award, title: 'Digital membership card', text: 'A premium members-only card with your role badges — proof you\'re part of the club.' },
  { icon: MessageCircle, title: 'Coaching that cares', text: 'Direct access to SPC coaching, session updates and club news the moment they happen.' },
];

export default function Auth() {
  const { state, user, authTab, login, signup, logout, forgotPassword, busy } = useSpc();
  const mode = state.auth?.mode || 'login';
  const [showDiscount, setShowDiscount] = React.useState(false);
  const [showExplainer, setShowExplainer] = React.useState(false);
  const downOnBackdrop = React.useRef(false);

  React.useEffect(() => {
    if (!showExplainer) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowExplainer(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showExplainer]);

  if (user) {
    return (
      <div className="pending-wrap" data-testid="pending-approval-screen">
        <motion.div className="card pending-card" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}>
          <div className="pending-icon">✓</div>
          <div className="eyebrow">Account created</div>
          <h2>Waiting for SPC approval</h2>
          <p>Thanks {user.name}. Your account is created, but the SPC Clubhouse is invite-grade. An SPC admin will approve your membership before you get in.</p>
          <div className="notice"><strong>{user.email}</strong><br /><span className="meta">Status: Awaiting approval</span></div>
          <button className="btn secondary" style={{ marginTop: 18 }} onClick={logout}>Back to sign in</button>
        </motion.div>
      </div>
    );
  }

  const onLogin = (e) => { e.preventDefault(); const f = new FormData(e.target); login(f.get('email'), f.get('password'), f.get('remember') !== null); };
  const onSignup = (e) => { e.preventDefault(); const f = new FormData(e.target); signup(f.get('name'), f.get('email'), f.get('password'), f.get('referralCode'), f.get('discountCode')); };
  const onForgot = (e) => { e.preventDefault(); const f = new FormData(e.target); forgotPassword(f.get('email')); };

  return (
    <div className="auth-shell">
      <div className="auth-brand-panel">
        <div className="abp-veil" />
        <div className="abp-orb o1" />
        <div className="abp-orb o2" />
        <motion.div className="abp-top" initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}>
          <Logo size={52} />
          <div><h1>SPC Clubhouse</h1><p>Smart Performance Coaching</p></div>
        </motion.div>
        <motion.div className="abp-hero" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .6, delay: .15 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>More than a booking app</div>
          <h2>Real coaching.<br />Real community.<br /><span className="hl" style={{ whiteSpace: 'nowrap' }}>Real results.</span></h2>
          <p>Coaching, sessions, racing, rewards and a community that shows up. Your entire SPC world in one powerful place.</p>
        </motion.div>
      </div>

      <div className="auth-form-panel">
        <motion.div className="auth-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .55, delay: .1 }}>
          <div className="a-head">
            <h2>{mode === 'login' ? 'Welcome back' : mode === 'forgot' ? 'Reset your password' : 'Join the club'}</h2>
            <p>{mode === 'login' ? 'Sign in to your SPC Clubhouse.' : mode === 'forgot' ? "Enter your email and we'll send you a reset link." : 'Join for £29/year — pay once, instant access.'}</p>
          </div>
          {mode !== 'forgot' && (
            <div className="auth-tabs">
              <button data-testid="auth-tab-login" className={mode === 'login' ? 'active' : ''} onClick={() => authTab('login')}>{mode === 'login' && <motion.span layoutId="authGlow" className="at-glow" />}Sign in</button>
              <button data-testid="auth-tab-signup" className={mode === 'signup' ? 'active' : ''} onClick={() => authTab('signup')}>{mode === 'signup' && <motion.span layoutId="authGlow" className="at-glow" />}Create account</button>
            </div>
          )}
          {mode === 'login' ? (
            <>
              <form className="form" onSubmit={onLogin}>
                <label>Email<input data-testid="login-email-input" name="email" type="email" autoComplete="email" required placeholder="you@example.com" /></label>
                <label>Password<input data-testid="login-password-input" name="password" type="password" autoComplete="current-password" required placeholder="Your password" /></label>
                <label data-testid="remember-me-toggle" style={{ display: 'flex', alignItems: 'center', gap: 9, flexDirection: 'row', cursor: 'pointer', fontSize: 13.5, opacity: 0.9 }}>
                  <input type="checkbox" name="remember" defaultChecked style={{ width: 16, height: 16, accentColor: '#e1e20a', flexShrink: 0 }} />
                  <span>Remember me — keep me signed in on this device</span>
                </label>
                <button data-testid="login-submit-btn" className="btn" style={{ marginTop: 4 }} disabled={!!busy}>{busy ? 'Signing in…' : 'Sign in to SPC'}</button>
                <button type="button" data-testid="forgot-password-link" className="link-btn" style={{ marginTop: 10, background: 'none', border: 'none', color: '#a4a8ad', fontSize: 13, cursor: 'pointer', textAlign: 'center', textDecoration: 'underline' }} onClick={() => authTab('forgot')}>Forgot password?</button>
              </form>
            </>
          ) : mode === 'forgot' ? (
            <>
              <form className="form" onSubmit={onForgot}>
                <label>Email<input data-testid="forgot-email-input" name="email" type="email" autoComplete="email" required placeholder="you@example.com" /></label>
                <button data-testid="forgot-submit-btn" className="btn" style={{ marginTop: 4 }} disabled={!!busy}>{busy ? 'Sending…' : 'Send reset link'}</button>
                <button type="button" data-testid="back-to-login-link" className="link-btn" style={{ marginTop: 10, background: 'none', border: 'none', color: '#a4a8ad', fontSize: 13, cursor: 'pointer', textAlign: 'center', textDecoration: 'underline' }} onClick={() => authTab('login')}>Back to sign in</button>
              </form>
            </>
          ) : (
            <>
              <form className="form" onSubmit={onSignup}>
                <label>Your name<input data-testid="signup-name-input" name="name" required autoComplete="name" placeholder="Full name" /></label>
                <label>Email<input data-testid="signup-email-input" name="email" type="email" required autoComplete="email" placeholder="you@example.com" /></label>
                <label>Create password<input data-testid="signup-password-input" name="password" type="password" minLength={6} required autoComplete="new-password" placeholder="Minimum 6 characters" /></label>
                <label>Referral code <span style={{ color: '#a4a8ad', fontWeight: 400, fontSize: 12 }}>(optional)</span><input data-testid="signup-referral-input" name="referralCode" placeholder="Add existing member's referral PIN here" style={{ textTransform: 'uppercase' }} /></label>
                {!showDiscount ? (
                  <button type="button" data-testid="show-discount-code-link" className="link-btn" style={{ background: 'none', border: 'none', color: '#a4a8ad', fontSize: 13, cursor: 'pointer', textAlign: 'left', textDecoration: 'underline', padding: 0 }} onClick={() => setShowDiscount(true)}>Have a discount code?</button>
                ) : (
                  <label>Discount code <span style={{ color: '#a4a8ad', fontWeight: 400, fontSize: 12 }}>(optional)</span><input data-testid="signup-discount-input" name="discountCode" placeholder="e.g. FREE2026" style={{ textTransform: 'uppercase' }} /></label>
                )}
                <button data-testid="signup-submit-btn" className="btn" style={{ marginTop: 4 }} disabled={!!busy}>{busy ? 'Please wait…' : 'Continue to payment · £29/year'}</button>
              </form>
              <div className="auth-note">One membership fee of <strong>£29 for the year</strong> — no extra sign-up fee. Paying activates your account instantly; there's no waiting for approval.</div>
            </>
          )}
        </motion.div>

        <button type="button" className="ax-trigger" data-testid="auth-explainer-trigger" onClick={() => setShowExplainer(true)}>
          <span className="ax-trigger-icon"><Sparkles size={17} /></span>
          <span className="ax-trigger-text">
            <strong>New here? See what SPC Club is about</strong>
            <span>Cost, benefits &amp; what you get — 30 second read</span>
          </span>
          <span className="ax-trigger-arrow"><ArrowRight size={17} /></span>
        </button>
      </div>

      {showExplainer && (
        <div
          className="ax-overlay"
          data-testid="auth-explainer-overlay"
          onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
          onMouseUp={(e) => { if (downOnBackdrop.current && e.target === e.currentTarget) setShowExplainer(false); }}
        >
          <motion.div className="ax-panel" data-testid="auth-explainer-section" initial={{ opacity: 0, y: 16, scale: .98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: .3 }}>
            <button type="button" className="ax-close" data-testid="auth-explainer-close" onClick={() => setShowExplainer(false)}>×</button>
            <div className="ax-head">
              <div className="eyebrow">New here?</div>
              <h3>What is SPC Club?</h3>
              <p>SPC Club is Smart Performance Coaching's members-only clubhouse — one place to book sessions, race with the crew, chase challenges and unlock real rewards. It's built for runners and athletes who want more than just a training plan; you get a whole community that trains, races and celebrates together.</p>
            </div>

            <div className="ax-price" data-testid="auth-explainer-price">
              <div className="ax-price-amount">£29<span>/year</span></div>
              <div className="ax-price-copy">
                <strong>One simple annual fee — that's it.</strong>
                <span>Works out at less than £2.50 a month. No hidden extras, no monthly billing surprises — pay once, get instant access to everything below.</span>
              </div>
            </div>

            <div className="ax-grid">
              {EXPLAINER_POINTS.map((p, i) => (
                <div className="ax-item" key={i} data-testid={`auth-explainer-point-${i}`}>
                  <div className="ax-icon"><p.icon size={18} /></div>
                  <div>
                    <div className="ax-item-title">{p.title}</div>
                    <div className="ax-item-text">{p.text}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="ax-foot">
              <CheckCircle2 size={16} />
              <span>Ready when you are — create your account above and pay securely to join instantly.</span>
            </div>

            <button type="button" className="btn" style={{ marginTop: 16, width: '100%' }} onClick={() => setShowExplainer(false)}>Got it</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
